import "./OnboardingPortal.scss";
import AppOnboardingForm from "./AppOnboardingForm";
import QnAOnboardingForm from "./QnAOnboardingForm";
import { useTranslation } from "@me8eon/translation";
import ManageAppsScreen from "./screens/ManageAppsScreen";
import ManageQnAScreen from "./screens/ManageQnAScreen";
import OnboardingDashboard from "./screens/OnboardingDashboard";
import DefaultAppsScreen from "./screens/DefaultAppsScreen";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { OnboardingPortalHeader } from "./OnboardingPortalHeader";
import useOnboardingPortalStore from "./OnboardingPortal.Store";
import ReviewerDashboard from "./screens/ReviewerDashboard";
import ReviewAppsScreen from "./screens/ReviewAppsScreen";
import ReviewQnAScreen from "./screens/ReviewQnAScreen";
import ReviewAppsModal from "./screens/ReviewAppsModal";
import ReviewQnAModal from "./screens/ReviewQnAModal";
import ReviewDefaultAppActivityScreen from "./screens/ReviewDefaultAppActivityScreen";
import DefaultAppActivityScreen from "./screens/DefaultAppActivityScreen";
import { useWindowUrlData } from "@me8eon/routing";
import { useEffect } from "react";
import { ModuleType } from "./OnboardingPortal.types";
import {
    ROLE_ONBOARDING_PORTAL_USER,
    ROLE_ONBOARDING_PORTAL_REVIEWER,
} from "./OnboardingPortal.constants";
import ViewAppsModal from "./screens/ViewAppsModal";
import ViewQnAModal from "./screens/ViewQnAModal";

export type { OnboardingAppData } from "./OnboardingPortal.types";

const OnboardingPortal = () => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const { actualRoles } = useUserData();
    const [urlData] = useWindowUrlData();
    const {
        showModule,
        setShowModule,
        editAppData,
        editQnAData,
        resetEditApp,
        resetEditQnA,
        fetchOnboardedApps,
        fetchOnboardedQnA,
    } = useOnboardingPortalStore();

    const isOnboardingPortalUser = () =>
        actualRoles.includes(ROLE_ONBOARDING_PORTAL_USER);
    const isOnboardingPortalReviewer = () =>
        actualRoles.includes(ROLE_ONBOARDING_PORTAL_REVIEWER);

    const renderCurrentModule = () => {
        switch (showModule) {
            case "home":
                return isOnboardingPortalUser() ? (
                    <OnboardingDashboard />
                ) : null;
            case "onboard-apps":
                return isOnboardingPortalUser() ? (
                    <AppOnboardingForm
                        onSubmit={() => {
                            setShowModule("manage-apps");
                        }}
                    />
                ) : null;
            case "onboard-qna":
                return isOnboardingPortalUser() ? (
                    <QnAOnboardingForm
                        onSubmit={() => {
                            // setShowModule("manage-qna");
                        }}
                    />
                ) : null;
            case "edit-apps":
                return isOnboardingPortalUser() ? (
                    <AppOnboardingForm
                        appData={editAppData}
                        onCancel={() => resetEditApp()}
                        onSubmit={() => {
                            fetchOnboardedApps(jwt);
                            resetEditApp();
                        }}
                    />
                ) : null;
            case "edit-qna":
                return isOnboardingPortalUser() ? (
                    <QnAOnboardingForm
                        qnaData={editQnAData}
                        onCancel={() => resetEditQnA()}
                        isEditing={editQnAData !== null}
                        onSubmit={() => {
                            fetchOnboardedQnA(jwt);
                            resetEditQnA();
                        }}
                    />
                ) : null;
            case "manage-apps":
                return isOnboardingPortalUser() ? <ManageAppsScreen /> : null;
            case "manage-qna":
                return isOnboardingPortalUser() ? <ManageQnAScreen /> : null;
            case "review-dashboard":
                return isOnboardingPortalReviewer() ? (
                    <ReviewerDashboard />
                ) : null;
            case "review-apps":
                return isOnboardingPortalReviewer() ? (
                    <>
                        <ReviewAppsScreen />
                        <div style={{ marginTop: "2rem" }} />
                        <ReviewDefaultAppActivityScreen />
                    </>
                ) : null;
            case "review-qna":
                return isOnboardingPortalReviewer() ? (
                    <ReviewQnAScreen />
                ) : null;
            case "default-apps":
                return isOnboardingPortalUser() ? (
                    <>
                        <DefaultAppsScreen />
                        <div style={{ marginTop: "2rem" }} />
                        <DefaultAppActivityScreen />
                    </>
                ) : null;
            default:
                return null;
        }
    };

    useEffect(() => {
        const view = urlData.url.searchParams.get("view");
        if (view) {
            if (
                ([
                    "home",
                    "onboard-apps",
                    "onboard-qna",
                    "edit-apps",
                    "edit-qna",
                    "manage-apps",
                    "manage-qna",
                    "default-apps",
                ].includes(view) &&
                    isOnboardingPortalUser()) ||
                (["review-dashboard", "review-apps", "review-qna"].includes(
                    view
                ) &&
                    isOnboardingPortalReviewer())
            ) {
                setShowModule(view as ModuleType);
                return;
            }
        }
    }, [urlData]);

    return (
        <div className="onboarding-portal">
            <OnboardingPortalHeader />
            <div className="onboarding-portal--content">
                {renderCurrentModule()}
            </div>
            {(showModule === "review-dashboard" ||
                showModule === "review-apps") && <ReviewAppsModal />}
            {(showModule === "review-dashboard" ||
                showModule === "review-qna") && <ReviewQnAModal />}
            <ViewAppsModal />
            <ViewQnAModal />
        </div>
    );
};

export default OnboardingPortal;
